/**
 * The file itself (FR-18, D21) — previewed **in the browser** from the logged,
 * five-minute presigned read; the service never renders content — but for a
 * TIFF, which the engine draws (below).
 *
 * - **PDF** in an `<iframe>`, **images** in an `<img>`, from the presigned URL.
 * - **TIFF** (engine W16, T16.19, C-E12): Chrome, Edge and Firefox draw none,
 *   so each page is the PNG the engine's sandbox draws of it, page by page,
 *   each a logged read (`page-drawings.ts`, shared with the review screen).
 * - **Spreadsheets** (XLSX, XLS, CSV): the first sheet as a grid, read by the
 *   kit's `readXlsxRows` **inside `preview.worker.ts`** — the file is untrusted
 *   and the kit's `xlsx` has known CVEs, so it is parsed off the page, capped in
 *   bytes before it is fetched, in rows and columns after, and in time.
 * - **Word documents**, **JSON** (W10, FR-44) and **plain text** (engine W18,
 *   FR-47 — a TRACES export) are download-only.
 * - **An encrypted PDF** (W22, FR-58, D56) is drawn by pdf.js on a canvas — an
 *   `<iframe>` cannot take a password — with the password an unlock proved,
 *   kept in this tab's memory (`password-keeper.ts`), or one typed into the
 *   preview's box. The box opens the pages **in this browser only**: the file's
 *   logged read as always, and nothing else — no parse, no password sent. The
 *   stored file stays exactly as uploaded, and its download stays protected.
 * - **An encrypted PDF whose password is saved** (W30, FR-67, ADR-0021) is
 *   drawn **by the engine**, page by page — the TIFF path — with the sealed
 *   password on the server: nothing is asked, and the browser never holds the
 *   password or a decrypted file. Should the service say none is saved after
 *   all (`423`), the preview falls back to the box above.
 *
 * No read is logged for a preview that cannot be shown: an encrypted file
 * without its password, a Word document, a JSON or text file or a spreadsheet
 * over the cap is said so without fetching it.
 */
import { Alert, Button, DataTable, Skeleton, SkeletonRegion, type ColumnDef } from '@fiveox/ui'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { fileKindOf, formatBytes, passwordSavedOf, DOCX, JSON_MEDIA_TYPE, NEVER_OPENED_MEDIA_TYPES, TEXT_MEDIA_TYPE, type FileHandle, type ProDocClient, type ProDocDocument } from './prodoc-client'
import { FileMark, plural } from './bits'
import { PagesPasswordBox, PdfRefusal, openPdfWithPdfjs, type PdfDocumentHandle, type PdfOpener } from './evidence-page'
import { errorText } from './hooks'
import { TIFF, openImageInBrowser, usePageDrawings, type ImageOpener } from './page-drawings'
import { passwordKeeper, useKeptPassword } from './password-keeper'

/** How much of a spreadsheet the preview reads and shows. */
export interface SheetCaps {
  /** The file's size; beyond it the preview says "too large" without fetching it. */
  maxBytes: number
  maxRows: number
  maxColumns: number
  /** A cell's characters. */
  maxCell: number
  /** How long the worker may take before it is stopped. */
  timeoutMs: number
}

export const SHEET_CAPS: SheetCaps = { maxBytes: 5 * 1024 * 1024, maxRows: 200, maxColumns: 20, maxCell: 200, timeoutMs: 15_000 }

/** A first sheet, capped: the columns shown, the rows shown, and how many there were. */
export interface SheetGrid {
  columns: string[]
  rows: Record<string, string>[]
  totalRows: number
  totalColumns: number
}

/** Why a sheet could not be shown: over the cap, unreadable, or the worker ran out of time. */
export class SheetRefusal extends Error {
  constructor(readonly reason: 'too_large' | 'unreadable' | 'timeout' | 'no_worker') {
    super(reason)
    this.name = 'SheetRefusal'
  }
}

/** Reads a spreadsheet's first sheet. The default runs the kit's reader in a Web Worker; a test passes its own. */
export type SheetReader = (bytes: ArrayBuffer, caps: SheetCaps) => Promise<SheetGrid>

/** The default reader: `preview.worker.ts`, one worker per file, terminated when it answers or runs out of time. */
export const readSheetInWorker: SheetReader = (bytes, caps) =>
  new Promise<SheetGrid>((resolve, reject) => {
    if (typeof Worker === 'undefined') {
      reject(new SheetRefusal('no_worker'))
      return
    }
    const worker = new Worker(new URL('./preview.worker.ts', import.meta.url), { type: 'module' })
    const timer = setTimeout(() => {
      worker.terminate()
      reject(new SheetRefusal('timeout'))
    }, caps.timeoutMs)
    const finish = () => {
      clearTimeout(timer)
      worker.terminate()
    }
    worker.onmessage = (event: MessageEvent<{ ok: true; grid: SheetGrid } | { ok: false; reason: 'too_large' | 'unreadable' }>) => {
      finish()
      if (event.data.ok) resolve(event.data.grid)
      else reject(new SheetRefusal(event.data.reason))
    }
    worker.onerror = () => {
      finish()
      reject(new SheetRefusal('unreadable'))
    }
    worker.postMessage({ bytes, caps }, [bytes])
  })

type State =
  | { kind: 'loading' }
  | { kind: 'none'; reason: string; download?: boolean }
  | { kind: 'too_large'; reason: string }
  | { kind: 'error'; reason: string }
  | { kind: 'pdf' | 'image'; url: string }
  | { kind: 'sheet'; grid: SheetGrid }

export interface PreviewProps {
  client: ProDocClient
  doc: ProDocDocument
  /** Offered where the file is download-only (a Word document) or cannot be shown. */
  onDownload?: () => void
  /** The spreadsheet reader — the Web Worker unless a test says otherwise. */
  readSheet?: SheetReader
  caps?: Partial<SheetCaps>
  /** How a TIFF's pages, drawn by the engine as PNGs, are decoded — the browser's own decoder unless a host or a test says otherwise. */
  openImage?: ImageOpener
  /** W22 (D56): how an encrypted PDF is opened with its password — PDF.js unless a host or a test says otherwise. */
  openPdf?: PdfOpener
  /**
   * W30 (FR-67): the current version's password is saved — its pages are the
   * engine's drawings. Omitted, the document says (`passwordSavedOf`); a host
   * holding the versions passes the current one's `password_saved`.
   */
  passwordSaved?: boolean
}

/** The file beside its record — shared by the detail and `<ProDocReview>`, whose reviewer checks the record against it. */
export function Preview(props: PreviewProps) {
  const key = `${props.doc.id}\n${props.doc.current_version_id}`
  // W30: the version whose pages the service would not draw after all (`423`) — its box asks instead.
  const [refused, setRefused] = useState<string | null>(null)
  // A TIFF is drawn page by page by the engine (T16.19); a new version of another kind is a new preview.
  if (props.doc.media_type === TIFF && !props.doc.encrypted) return <TiffPreview key={key} {...props} />
  // W30 (FR-67): an encrypted PDF with its password saved — the engine draws its pages with it; nothing is asked here.
  const saved = (props.passwordSaved ?? passwordSavedOf(props.doc)) && refused !== key
  if (props.doc.media_type === 'application/pdf' && props.doc.encrypted && saved) return <TiffPreview key={key} {...props} saved onLocked={() => setRefused(key)} />
  // An encrypted PDF opens with its password in the browser (D56); each version has its own.
  if (props.doc.media_type === 'application/pdf' && props.doc.encrypted) return <EncryptedPdfPreview key={key} {...props} />
  return <FilePreview {...props} />
}

type Locked = { kind: 'ask' } | { kind: 'loading' } | { kind: 'ready'; pdf: PdfDocumentHandle } | { kind: 'none'; reason: string } | { kind: 'error'; reason: string }

const LOCKED_TEXT = 'Encrypted — type its password to see its pages here, in this browser only. Nothing is sent, and the file stays protected.'

/**
 * An encrypted PDF (FR-58, D56): its pages drawn by pdf.js with the password
 * kept in this tab, or with one typed into the box — which sends nothing but
 * the file's logged read. A typed password that opens it is kept for this tab;
 * a wrong one is said and dropped, and a kept one that no longer opens it is
 * forgotten, so the box asks again.
 */
function EncryptedPdfPreview({ client, doc, onDownload, openPdf = openPdfWithPdfjs }: PreviewProps) {
  const versionId = doc.current_version_id
  const kept = useKeptPassword(versionId)
  const [typed, setTyped] = useState<string | null>(null)
  const [wrong, setWrong] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<Locked>({ kind: 'ask' })
  const password = typed ?? kept

  useEffect(() => {
    if (!password) {
      // Said without fetching: no read is logged until a password is there to open it.
      setState({ kind: 'ask' })
      return
    }
    let cancelled = false
    let opened: PdfDocumentHandle | null = null
    setState({ kind: 'loading' })
    void client.documents
      .file(doc.id, 'view')
      .then((handle) => handle.blob())
      .then(async (blob) => openPdf(new Uint8Array(await blob.arrayBuffer()), { password }))
      .then((pdf) => {
        if (cancelled) {
          pdf.destroy()
          return
        }
        opened = pdf
        if (typed) passwordKeeper.keep(versionId, typed)
        setTyped(null)
        setState({ kind: 'ready', pdf })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof PdfRefusal && (e.reason === 'wrong_password' || e.reason === 'encrypted')) {
          if (typed) {
            setTyped(null)
            setWrong(true)
          } else passwordKeeper.forget(versionId)
          setState({ kind: 'ask' })
          return
        }
        if (e instanceof PdfRefusal) setState({ kind: 'none', reason: 'This PDF could not be drawn here. Download it to open it with its password.' })
        else setState({ kind: 'error', reason: errorText(e, 'The file could not be fetched.') })
      })
    return () => {
      cancelled = true
      opened?.destroy()
    }
    // `typed` is read through `password`: tried once, then kept or dropped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, doc.id, versionId, openPdf, password, attempt])

  if (state.kind === 'loading') return <Skeleton className="h-[60vh] w-full rounded-lg" />
  if (state.kind === 'ready') return <PdfCanvasPages pdf={state.pdf} title={doc.title} />
  if (state.kind === 'error') {
    return (
      <Alert tone="error">
        {state.reason}{' '}
        <Button variant="link" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </Button>
      </Alert>
    )
  }
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center" data-preview={state.kind === 'ask' ? 'locked' : 'none'}>
      <FileMark mediaType={doc.media_type} />
      <p className="text-foreground text-sm font-medium">{doc.file_name}</p>
      <p className="text-muted-foreground max-w-sm text-xs">{state.kind === 'none' ? state.reason : LOCKED_TEXT}</p>
      {state.kind === 'ask' && (
        <PagesPasswordBox
          wrong={wrong}
          onTyping={() => setWrong(false)}
          onOpen={(value) => {
            setWrong(false)
            setTyped(value)
          }}
        />
      )}
      {onDownload && (
        <Button variant="outline" size="sm" onClick={onDownload}>
          <Download size={14} aria-hidden />
          Download the protected file
        </Button>
      )}
    </div>
  )
}

/** An opened PDF, a page at a time, as wide as its place — the preview's own pager, no evidence boxes. */
function PdfCanvasPages({ pdf, title }: { pdf: PdfDocumentHandle; title: string }) {
  const [shown, setShown] = useState(1)
  const [width, setWidth] = useState(0)
  const [failed, setFailed] = useState(false)
  const frame = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const el = frame.current
    if (!el) return
    setWidth(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setWidth(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    void pdf
      .size(shown)
      .then((size) => {
        if (cancelled || !canvas.current || !(size.width > 0)) return
        // A sensible width where the browser cannot measure one.
        return pdf.render(shown, canvas.current, (width > 0 ? width : 640) / size.width)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [pdf, shown, width])

  return (
    <div>
      <div className="mb-3 flex items-center gap-1 text-sm">
        <Button variant="ghost" size="sm" disabled={shown <= 1} onClick={() => setShown((n) => Math.max(1, n - 1))} aria-label="Previous page">
          <ChevronLeft size={16} aria-hidden />
        </Button>
        <span className="text-foreground font-medium">
          Page {shown} <span className="text-muted-foreground font-normal">of {pdf.pages}</span>
        </span>
        <Button variant="ghost" size="sm" disabled={shown >= pdf.pages} onClick={() => setShown((n) => Math.min(pdf.pages, n + 1))} aria-label="Next page">
          <ChevronRight size={16} aria-hidden />
        </Button>
      </div>
      <div ref={frame} className="w-full">
        {failed ? (
          <p className="text-muted-foreground text-sm">This page could not be drawn here. Download the file to open it.</p>
        ) : (
          <canvas ref={canvas} aria-label={`Page ${shown} of ${title}`} className="border-border block h-auto w-full rounded-lg border" data-preview-page={shown} />
        )}
      </div>
    </div>
  )
}

const PAGE_MISSING = 'This page is not in the file.'
const PAGE_UNREADABLE = 'This page could not be drawn here. Download the file to open it.'

/**
 * A TIFF (T16.19, C-E12, D44): Chrome, Edge and Firefox draw none, so each page
 * is the PNG the engine draws of it — the current version's, page by page,
 * each a logged read when it is first shown (`page-drawings.ts`). The record
 * says how many pages there are when the service counted them; otherwise the
 * pager learns the last one when the page after it is not in the file. No
 * evidence boxes here — that is the extraction's screen.
 */
function TiffPreview({ client, doc, onDownload, openImage = openImageInBrowser, saved = false, onLocked }: PreviewProps & { saved?: boolean; onLocked?: () => void }) {
  const versionId = doc.current_version_id
  const [shown, setShown] = useState(1)
  const [last, setLast] = useState<number | null>(null)
  const drawings = usePageDrawings(client, doc.id, versionId, true, openImage)
  const drawPage = drawings.draw
  const drawing = drawings.pages.get(shown) ?? null
  const missing = drawing?.kind === 'none' && drawing.reason === 'missing'
  const locked = drawing?.kind === 'none' && drawing.reason === 'locked'

  // W30: no password is saved after all — the preview asks for one instead.
  useEffect(() => {
    if (locked) onLocked?.()
  }, [locked, onLocked])

  useEffect(() => {
    drawPage(shown)
  }, [shown, drawPage])

  // A page past the end says where the end is.
  useEffect(() => {
    if (missing && shown > 1) setLast((known) => (known === null ? shown - 1 : Math.min(known, shown - 1)))
  }, [missing, shown])

  const count = doc.page_count && doc.page_count > 0 ? doc.page_count : last
  const label = count !== null && shown <= count ? `Page ${shown} of ${count}` : `Page ${shown}`

  return (
    <div>
      <div className="mb-3 flex items-center gap-1 text-sm">
        <Button variant="ghost" size="sm" disabled={shown <= 1} onClick={() => setShown((n) => Math.max(1, n - 1))} aria-label="Previous page">
          <ChevronLeft size={16} aria-hidden />
        </Button>
        <span className="text-foreground font-medium">{label}</span>
        <Button variant="ghost" size="sm" disabled={missing || (count !== null && shown >= count)} onClick={() => setShown((n) => n + 1)} aria-label="Next page">
          <ChevronRight size={16} aria-hidden />
        </Button>
      </div>
      {drawing?.kind === 'ready' ? (
        <img
          src={drawing.image.url}
          alt={`Page ${shown} of ${doc.title}`}
          className="border-border max-h-[70vh] w-full rounded-lg border object-contain"
          data-preview-page={shown}
          data-preview-source="engine"
        />
      ) : drawing?.kind === 'error' ? (
        <Alert tone="error">
          {drawing.reason}{' '}
          <Button variant="link" onClick={() => drawPage(shown, true)}>
            Try again
          </Button>
        </Alert>
      ) : drawing?.kind === 'none' ? (
        <div className="flex h-[40vh] flex-col items-center justify-center gap-3 text-center" data-preview="none">
          <FileMark mediaType={doc.media_type} />
          <p className="text-foreground text-sm font-medium">{doc.file_name}</p>
          <p className="text-muted-foreground max-w-sm text-xs">
            {missing ? `${PAGE_MISSING}${count !== null ? ` It has ${plural(count, 'page')}.` : ''}` : PAGE_UNREADABLE}
          </p>
          {onDownload && !missing && (
            <Button variant="outline" size="sm" onClick={onDownload}>
              <Download size={14} aria-hidden />
              Download
            </Button>
          )}
        </div>
      ) : (
        <SkeletonRegion label={`Drawing page ${shown}`}>
          <Skeleton className="h-[60vh] w-full rounded-lg" />
        </SkeletonRegion>
      )}
      {saved && <p className="text-muted-foreground mt-2 text-xs">{SAVED_PAGES_NOTE}</p>}
    </div>
  )
}

/** W30: under an encrypted PDF's pages drawn with its saved password. */
const SAVED_PAGES_NOTE = 'Opened with its saved password. The file stays protected — its download still needs the password.'

/** Every other file: the browser draws it from the logged, presigned read — or says why not. */
function FilePreview({ client, doc, onDownload, readSheet = readSheetInWorker, caps: capsIn }: PreviewProps) {
  const caps = { ...SHEET_CAPS, ...capsIn }
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const kind = fileKindOf(doc.media_type)
    if (doc.encrypted) {
      setState({ kind: 'none', reason: 'Encrypted — download it and open it with its password.', download: true })
      return
    }
    if (doc.media_type === DOCX) {
      setState({ kind: 'none', reason: 'Word documents are not previewed here — download it to open it.', download: true })
      return
    }
    if (doc.media_type === JSON_MEDIA_TYPE) {
      setState({ kind: 'none', reason: 'JSON files are not previewed here — download it to open it.', download: true })
      return
    }
    if (doc.media_type === TEXT_MEDIA_TYPE) {
      setState({ kind: 'none', reason: 'Text files are not previewed here — download it to open it.', download: true })
      return
    }
    if (NEVER_OPENED_MEDIA_TYPES.includes(doc.media_type)) {
      // W31 (FR-68, ADR-0022): an e-mail or a ZIP is kept as given and never opened — not even to list what it holds.
      setState({ kind: 'none', reason: 'No preview — download to open.', download: true })
      return
    }
    if (kind !== 'pdf' && kind !== 'image' && kind !== 'sheet') {
      setState({ kind: 'none', reason: 'No preview for this kind of file — download it to open it.', download: true })
      return
    }
    if (kind === 'sheet' && doc.size_bytes > caps.maxBytes) {
      // Said before a byte is fetched — and no read is logged for it.
      setState({ kind: 'too_large', reason: `Too large to preview — ${formatBytes(doc.size_bytes)}; a spreadsheet is previewed up to ${formatBytes(caps.maxBytes)}. Download it to open it.` })
      return
    }
    setState({ kind: 'loading' })
    void client.documents
      .file(doc.id, 'view')
      .then(async (handle: FileHandle) => {
        if (cancelled) return
        if (kind === 'sheet') {
          const blob = await handle.blob()
          if (blob.size > caps.maxBytes) throw new SheetRefusal('too_large')
          const grid = await readSheet(await blob.arrayBuffer(), caps)
          if (!cancelled) setState({ kind: 'sheet', grid })
          return
        }
        // The presigned address embeds for its five minutes; a blob URL when
        // the service handed the bytes themselves.
        const url = handle.url ?? (objectUrl = URL.createObjectURL(await handle.blob()))
        if (!cancelled) setState({ kind, url })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof SheetRefusal) {
          setState(
            e.reason === 'too_large'
              ? { kind: 'too_large', reason: `Too large to preview — a spreadsheet is previewed up to ${formatBytes(caps.maxBytes)}. Download it to open it.` }
              : e.reason === 'timeout'
                ? { kind: 'none', reason: 'The sheet took too long to read, so the preview stopped. Download it to open it.', download: true }
                : e.reason === 'no_worker'
                  ? { kind: 'none', reason: 'This browser cannot preview a spreadsheet safely. Download it to open it.', download: true }
                  : { kind: 'none', reason: 'The sheet could not be read. Download it to open it.', download: true },
          )
          return
        }
        setState({ kind: 'error', reason: errorText(e, 'The file could not be fetched.') })
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // The caps are constants per mount; the reader is the host's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, doc.id, doc.current_version_id, doc.media_type, doc.encrypted, doc.size_bytes, attempt])

  return (
    <div>
      {state.kind === 'loading' && <Skeleton className="h-[60vh] w-full rounded-lg" />}
      {state.kind === 'pdf' && <iframe src={state.url} title={`Preview of ${doc.title}`} className="border-border h-[70vh] w-full rounded-lg border" />}
      {state.kind === 'image' && <img src={state.url} alt={`Scan of ${doc.title}`} className="border-border max-h-[70vh] w-full rounded-lg border object-contain" />}
      {state.kind === 'sheet' && <SheetPreview grid={state.grid} />}
      {state.kind === 'error' && (
        <Alert tone="error">
          {state.reason}{' '}
          <Button variant="link" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </Alert>
      )}
      {(state.kind === 'none' || state.kind === 'too_large') && (
        <div className="flex h-[40vh] flex-col items-center justify-center gap-3 text-center" data-preview={state.kind}>
          <FileMark mediaType={doc.media_type} />
          <p className="text-foreground text-sm font-medium">{doc.file_name}</p>
          <p className="text-muted-foreground max-w-sm text-xs">{state.reason}</p>
          {onDownload && (state.kind === 'too_large' || state.download) && (
            <Button variant="outline" size="sm" onClick={onDownload}>
              <Download size={14} aria-hidden />
              Download
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function SheetPreview({ grid }: { grid: SheetGrid }) {
  if (grid.rows.length === 0) {
    return <p className="text-muted-foreground text-sm">The first sheet is empty.</p>
  }
  // A detail panel: columns and rows only — no toolbar, no pager (UI_SYSTEM §13).
  const columns: ColumnDef<Record<string, string> & { __row: string }>[] = grid.columns.map((key) => ({
    key,
    label: key.replace(/_/g, ' '),
    width: 120,
    render: (row) => row[key] ?? '',
  }))
  const rows = grid.rows.map((r, i) => ({ ...r, __row: String(i) }))
  const cut = [grid.totalRows > grid.rows.length ? `the first ${grid.rows.length} of ${plural(grid.totalRows, 'row')}` : plural(grid.rows.length, 'row'), grid.totalColumns > grid.columns.length ? `${grid.columns.length} of ${grid.totalColumns} columns` : null]
  return (
    <div>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.__row} maxHeight="60vh" />
      <p className="text-muted-foreground mt-2 text-xs">The first sheet — {cut.filter(Boolean).join(', ')}.</p>
    </div>
  )
}
